import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyleWide, FormRow, DetailField, fmtMoney, fmtQty } from './ProcurementShared';

const emptyLine = () => ({ item_type: 'description', description: '', quantity: 1, unit: '', estimated_unit_cost: 0 });

export default function ProcurementPurchaseRequests() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/purchase-requests?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const viewPr = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-requests/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const doAction = async (id, action, extra = {}) => {
    if (action === 'reject' && !extra.reason) {
      const reason = window.prompt('Rejection reason (required):');
      if (!reason) return;
      extra = { ...extra, reason };
    }
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-requests/${id}/${action}`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(extra),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Action failed'); return; }
    fetchList();
    if (viewing) viewPr(id);
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this purchase request?')) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-requests/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📝 Purchase Requests</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Purchase Request</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search PR #/department..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="converted_to_po">Converted to PO</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['PR #', 'Department', 'Needed By', 'Priority', 'Est. Cost', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No purchase requests</td></tr>}
              {list.map(pr => (
                <tr key={pr.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}><a href="#view" onClick={e => { e.preventDefault(); viewPr(pr.id); }} style={{ color: '#3b82f6' }}>{pr.pr_number}</a></td>
                  <td style={tdStyle}>{pr.department_name || '—'}</td>
                  <td style={tdStyle}>{pr.needed_by_date ? new Date(pr.needed_by_date).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>{pr.priority}</td>
                  <td style={tdStyle}>{fmtMoney(pr.estimated_cost)}</td>
                  <td style={tdStyle}><Badge value={pr.status} /></td>
                  <td style={tdStyle}>
                    {pr.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(pr)}>Edit</button>}
                    {pr.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => removeItem(pr.id)}>Delete</button>}
                    <button style={btnSmall} onClick={() => viewPr(pr.id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <PrModal companyId={companyId} pr={editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
      {viewing && <PrDetail companyId={companyId} pr={viewing} onClose={() => setViewing(null)} onAction={doAction} />}
    </div>
  );
}

function PrModal({ companyId, pr, onClose, onSaved }) {
  const isNew = !pr.id;
  const [form, setForm] = useState({
    department_name: pr.department_name || '', needed_by_date: pr.needed_by_date ? pr.needed_by_date.substring(0, 10) : '',
    priority: pr.priority || 'normal', notes: pr.notes || '',
  });
  const [lines, setLines] = useState(pr.lines && pr.lines.length ? pr.lines.map(l => ({ ...l })) : [emptyLine()]);
  const [saving, setSaving] = useState(false);

  const updateLine = (i, field, val) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: val };
    setLines(next);
  };
  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const estimatedTotal = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.estimated_unit_cost) || 0), 0);

  const save = async () => {
    const validLines = lines.filter(l => l.description && Number(l.quantity) > 0);
    if (validLines.length === 0) { alert('At least one line item is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/procurement/purchase-requests` : `${API}/companies/${companyId}/procurement/purchase-requests/${pr.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lines: validLines }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Purchase Request' : `Edit ${pr.pr_number}`}</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Department"><input style={inputStyle} value={form.department_name} onChange={e => setForm({ ...form, department_name: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Needed By"><input type="date" style={inputStyle} value={form.needed_by_date} onChange={e => setForm({ ...form, needed_by_date: e.target.value })} /></FormRow></div>
          <div style={{ flex: 1 }}>
            <FormRow label="Priority">
              <select style={inputStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </FormRow>
          </div>
        </div>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>

        <h4 style={{ marginBottom: 8 }}>Line Items</h4>
        <table style={tableStyle}>
          <thead><tr>{['Description', 'Qty', 'Unit', 'Est. Unit Cost', 'Line Total', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={tdStyle}><input style={inputStyle} value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 80 }}><input type="number" style={inputStyle} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 80 }}><input style={inputStyle} value={l.unit || ''} onChange={e => updateLine(i, 'unit', e.target.value)} /></td>
                <td style={{ ...tdStyle, width: 110 }}><input type="number" style={inputStyle} value={l.estimated_unit_cost} onChange={e => updateLine(i, 'estimated_unit_cost', e.target.value)} /></td>
                <td style={tdStyle}>{fmtMoney((Number(l.quantity) || 0) * (Number(l.estimated_unit_cost) || 0))}</td>
                <td style={tdStyle}><button style={btnSmall} onClick={() => removeLine(i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button style={{ ...btnSecondary, marginTop: 8 }} onClick={addLine}>+ Add Line</button>

        <div style={{ textAlign: 'right', marginTop: 10, fontWeight: 700 }}>Estimated Total: {fmtMoney(estimatedTotal)}</div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function PrDetail({ pr, onClose, onAction }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{pr.pr_number} <Badge value={pr.status} /></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          <DetailField label="Department" value={pr.department_name} />
          <DetailField label="Needed By" value={pr.needed_by_date ? new Date(pr.needed_by_date).toLocaleDateString() : '—'} />
          <DetailField label="Priority" value={pr.priority} />
          <DetailField label="Estimated Cost" value={fmtMoney(pr.estimated_cost)} />
          <DetailField label="Approval Status" value={pr.approval_status} />
          <DetailField label="Notes" value={pr.notes} />
        </div>

        <h4>Line Items</h4>
        <table style={tableStyle}>
          <thead><tr>{['Description', 'Qty', 'Unit', 'Est. Unit Cost', 'Line Total'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(pr.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.description}</td>
                <td style={tdStyle}>{fmtQty(l.quantity)}</td>
                <td style={tdStyle}>{l.unit || '—'}</td>
                <td style={tdStyle}>{fmtMoney(l.estimated_unit_cost)}</td>
                <td style={tdStyle}>{fmtMoney((Number(l.quantity) || 0) * (Number(l.estimated_unit_cost) || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {(pr.approvals || []).length > 0 && (
          <>
            <h4>Approvals</h4>
            <table style={tableStyle}>
              <thead><tr>{['Level', 'Decision', 'Date'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {pr.approvals.map(a => (
                  <tr key={a.id}><td style={tdStyle}>{a.level}</td><td style={tdStyle}><Badge value={a.decision} /></td><td style={tdStyle}>{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {pr.status === 'draft' && <button style={btnPrimary} onClick={() => onAction(pr.id, 'submit')}>Submit</button>}
          {pr.status === 'submitted' && <button style={btnPrimary} onClick={() => onAction(pr.id, 'approve')}>Approve</button>}
          {pr.status === 'submitted' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => onAction(pr.id, 'reject')}>Reject</button>}
          {['draft', 'submitted', 'approved'].includes(pr.status) && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => onAction(pr.id, 'cancel')}>Cancel</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
